import { createBrowserRouter, Navigate } from "react-router";
import InquiriesPage from "./routes/inquiries";
import InquiryPage from "./routes/inquiry";
import JobPage, { OldJobPath } from "./routes/job";
import { OldInquiriesPath } from "./routes/old-inquiries-path";
import ForgotPasswordPage from "./routes/forgot-password";
import LoginPage from "./routes/login";
import ResetPasswordPage from "./routes/reset-password";
import VerifyEmailPage from "./routes/verify-email";
import NotFound from "./routes/not-found";
import SettingsPage from "./routes/settings";
import { AppShell } from "./components/ui/AppShell";

/**
 * Intake's admin SPA: one destination, the settings behind it, and one
 * page to sign in on.
 *
 * `/` redirects to the destination the way the RMS workspace's does.
 * It was the settings page itself until #85, because there was no list
 * to be the home page — a Company had nothing to browse here, since its
 * jobs live in whichever rental system it runs. What came in is the
 * exception: every call and every form submission is intake's own, and
 * most of them never became a job for a rental system to hold.
 *
 * `/settings` keeps its own path and its `?section=`, which is what
 * people have bookmarked, and which is why the redirect above points at
 * the list rather than the other way round.
 *
 * Opening one arrival is a place and not a piece of component state:
 * the kind is in the path because a call and a form submission are read
 * from different tables, so a browser never hands over a uuid and asks
 * which one to try. A job is a place too (#115), addressed by the
 * Backend Ref the Inquiry and every call about it share: it is what
 * the New Inquiry email's button opens and what the list's job rows
 * open, and an arrival about a job opened by its own path goes there.
 * It is under the destination it is opened from, `/enquiries/{ref}`;
 * it was `/jobs/{ref}` until #130 and `/inquiries/{ref}` until #140, and
 * each of those paths is on every job
 * already drafted into a Company's rental system (#121) and in every
 * New Inquiry email already sent, so it still lands on the page.
 *
 * `AppShell` owns the auth gate — a layout route rather than a
 * `RequireAuth` repeated per route, which matters more here than in the
 * RMS because a second route is one line away from forgetting it.
 *
 * Four routes sit outside it: `/login`, the two a person with a
 * forgotten password uses before they can log in, `/forgot-password` and
 * `/reset-password` (where the link in the email lands, and an invite
 * link too), and `/verify-email`, where the link confirming an address
 * lands, which may be opened in a browser signed in to nothing. A vendor redirect sat outside it as well while a Company on
 * its own Current RMS signed in by authorizing that account (ADR-0019,
 * ADR-0020); both are superseded, the authorization is gone, and every
 * Company signs in here the same way.
 */
export const router = createBrowserRouter([
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    path: "/verify-email",
    element: <VerifyEmailPage />,
  },
  {
    element: <AppShell />,
    children: [
      {
        path: "/",
        element: <Navigate to="/enquiries" replace />,
      },
      {
        path: "/enquiries",
        element: <InquiriesPage />,
      },
      {
        path: "/enquiries/:kind/:id",
        element: <InquiryPage />,
      },
      {
        path: "/enquiries/:ref",
        element: <JobPage />,
      },
      {
        path: "/jobs/:ref",
        element: <OldJobPath />,
      },
      {
        path: "/inquiries/*",
        element: <OldInquiriesPath />,
      },
      {
        path: "/settings",
        element: <SettingsPage />,
      },
    ],
  },
  {
    path: "*",
    element: <NotFound />,
  },
]);
